import { DeleteFilled, EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Col, App as AntApp, Result, Row, Space, Spin } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { StatePieceStatus } from '../../../../../enums';
import { ContentHeader } from '../../../../../shared/components/appSkeleton/contentHeader/ContentHeader';
import { FetchResult } from '../../../../../shared/components/fetchResult/FetchResult';
import { ROW_GUTTER } from '../../../../../shared/Constants';
import { isError } from '../../../../../shared/helpers';
import { AppDispatch, RootState } from '../../../../../store';
import { deleteCurrency, fetchCurrencyById } from '../../../../../store/lookupSlice/LookupActions';
import { resetState } from '../../../../../store/lookupSlice/LookupSlice';
import { NotFound } from '../../../../notFound/NotFound';

export const CurrencyLookupListDetail = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const { modal } = AntApp.useApp();
    const lookupSlice = useSelector((state: RootState) => state.lookupSlice);
    const isLoading =
        lookupSlice.detailsPage.currency.updateStatus === StatePieceStatus.IsFetching ||
        lookupSlice.detailsPage.currency.fetchStatus === StatePieceStatus.IsFetching;
    const itemId = params.id ? parseInt(params.id) : undefined;

    const removeCurrency = () => {
        modal.confirm({
            title: 'Delete currency',
            icon: <ExclamationCircleOutlined />,
            content: "Are you sure you want to delete this currency? This action can't be undone.",
            okText: 'Confirm',
            cancelText: 'Cancel',
            onOk: () => {
                if (itemId) dispatch(deleteCurrency(itemId));
            },
        });
    };

    useEffect(() => {
        if (lookupSlice.detailsPage.currency.deleteStatus === StatePieceStatus.Success) {
            navigate('/admin/lookup-lists/currency', { replace: true });
        }
    }, [lookupSlice.detailsPage.currency.deleteStatus]);

    useEffect(() => {
        if (itemId) {
            dispatch(fetchCurrencyById(itemId));
        }

        return () => {
            dispatch(resetState());
        };
    }, [itemId]);

    if (lookupSlice.detailsPage.currency.fetchStatus === StatePieceStatus.Error) {
        return <Result status="warning" title="Data wasn't found or something went wrong." />;
    }

    if (!itemId) {
        return <NotFound />;
    }

    return (
        <React.Fragment>
            <ContentHeader
                title={`Currency #${params.id}`}
                breadcrumbs={[
                    {
                        breadcrumbName: 'Admin',
                        path: '/admin',
                    },
                    {
                        breadcrumbName: 'Manage lookup lists',
                        path: '/admin/lookup-lists',
                    },
                    {
                        breadcrumbName: 'Currency',
                        path: '/admin/lookup-lists/currency',
                    },
                    {
                        breadcrumbName: lookupSlice.detailsPage.currency.data?.description || '-',
                        path: `/admin/lookup-lists/currency/${params.id}`,
                    },
                ]}
            >
                {!isError(lookupSlice.detailsPage.currency.fetchStatus) && (
                    <>
                        <Button type="primary" danger icon={<DeleteFilled />} loading={isLoading} onClick={() => removeCurrency()}>
                            Delete option
                        </Button>
                        <Button
                            type="default"
                            icon={<EditOutlined />}
                            onClick={() => navigate(`/admin/lookup-lists/currency/${params.id}/edit`)}
                        >
                            Edit option details
                        </Button>
                    </>
                )}
            </ContentHeader>

            <div className="page-content">
                <Spin spinning={lookupSlice.detailsPage.currency.fetchStatus === StatePieceStatus.IsFetching}>
                    {isError(lookupSlice.detailsPage.currency.fetchStatus) ? (
                        <FetchResult status={lookupSlice.detailsPage.currency.fetchStatus} about={'Currency'} />
                    ) : (
                        <>
                            <Row>
                                <Col span={24}>
                                    <Space>
                                        <label className="group-label">Currency option Information</label>
                                    </Space>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={6}>
                                    <label className="property required">Id</label>
                                    <p>{lookupSlice.detailsPage.currency?.data?.id || '-'}</p>
                                </Col>
                                <Col span={6}>
                                    <label className="property required">Code</label>
                                    <p>{lookupSlice.detailsPage.currency?.data?.code || '-'}</p>
                                </Col>
                                <Col span={6}>
                                    <label className="property required">Description</label>
                                    <p>{lookupSlice.detailsPage.currency?.data?.description || '-'}</p>
                                </Col>
                                <Col span={6}>
                                    <label className="property">Bank account</label>
                                    <p>{lookupSlice.detailsPage.currency?.data?.defaultBankAccount?.name || '-'}</p>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={6}>
                                    <label className="property required">Conversion rate</label>
                                    <p>{lookupSlice.detailsPage.currency?.data?.conversionRate || '-'}</p>
                                </Col>
                                <Col span={6}>
                                    <label className="property required">Conversion date</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.currency?.data?.conversionRateDate
                                            ? dayjs(lookupSlice.detailsPage.currency?.data?.conversionRateDate).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={12}>&nbsp;</Col>
                            </Row>

                            {/* INTERNAL INFORMATION */}
                            <Row className="margin-top">
                                <Col span={24}>
                                    <label className="group-label">Internal Information</label>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Created by</label>
                                    <p>{lookupSlice.detailsPage.currency.data?.createdBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Created on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.currency.data?.created
                                            ? dayjs(lookupSlice.detailsPage.currency.data?.created).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Last modified by</label>
                                    <p>{lookupSlice.detailsPage.currency.data?.modifiedBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Last modified on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.currency.data?.modified
                                            ? dayjs(lookupSlice.detailsPage.currency.data?.modified).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                        </>
                    )}
                </Spin>
            </div>
        </React.Fragment>
    );
};
